/**
 * Format Australian Business Number (ABN) with spaces
 * Format: XX XXX XXX XXX (e.g., "51 824 753 556")
 *
 * @param abn - Raw ABN string (11 digits, may contain spaces)
 * @returns Formatted ABN with spaces, or original value if invalid
 */
export function formatABN(abn: string | null | undefined): string {
  if (!abn) return "";

  // Normalize to digits-only so pre-existing separators don't break rendering.
  const digits = abn.replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";

  // Progressive mask while typing: XX XXX XXX XXX.
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
}

/**
 * Format Australian Company Number (ACN) with spaces
 * Format: XXX XXX XXX (e.g., "824 753 556")
 *
 * @param acn - Raw ACN string (9 digits, may contain spaces)
 * @returns Formatted ACN with spaces, or original value if invalid
 */
export function formatACN(acn: string | null | undefined): string {
  if (!acn) return "";

  // Normalize to digits-only so pre-existing separators don't break rendering.
  const digits = acn.replace(/\D/g, "").slice(0, 9);
  if (!digits) return "";

  // Progressive mask while typing: XXX XXX XXX.
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

/**
 * Strip all spaces from a string (for storing raw digits in database)
 *
 * @param value - String that may contain spaces
 * @returns String with all spaces removed
 */
export function stripSpaces(value: string | null | undefined): string {
  if (!value) return "";
  return value.replace(/\s/g, "");
}
